import { Component, ErrorInfo, ReactNode } from 'react';
import i18n from '@/i18n';
import { reportError } from '@/utils/errorReporter';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Only log in development — never expose stack traces to production console
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, info.componentStack);
    }
    reportError(error, info);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: 40, gap: 12,
          color: 'var(--text-secondary, #6b7280)',
        }}>
          <i
            className="fas fa-exclamation-triangle"
            style={{ fontSize: 32, color: 'var(--warning, #F08521)' }}
          />
          <h2 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary, #1f2937)' }}>
            {i18n.t('common.errorBoundaryTitle')}
          </h2>
          <p style={{ margin: 0, fontSize: 13 }}>
            {import.meta.env.DEV ? this.state.error?.message : i18n.t('common.errorGeneric')}
          </p>
          <button
            onClick={this.handleRetry}
            style={{
              marginTop: 8, padding: '6px 16px', borderRadius: 8, border: 'none',
              background: 'var(--cp-purple, #342F82)', color: '#fff',
              cursor: 'pointer', fontSize: 13,
            }}
          >
            {i18n.t('common.retry')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
